import { useMemo } from 'react'
import moment from 'moment'
import useAllCharterFlightJourneys from 'hooks/CharterFlights/useAllCharterFlightJourneys'
import { toBaseFareClass } from 'constants/flight'
import { getEffectiveFlightPrice } from 'lib/flights/flightUtils'
import type { UseBundleFlightDataParams, BundleFlightData, FlightDataByDate } from './types'
import {
  getFnfSeatsLeftFromJourney,
  getJourneyOriginalTotalFareForFareClass,
  getPromotionSeatsLeftFromJourney,
  getSeatsLeftFromJourney,
  isRoundTripFnfPriceApplied,
} from './utils'

/**
 * Custom hook to handle flight-only data using charter flight journeys
 *
 * This hook handles:
 * - Fetching all charter flight journeys using useAllCharterFlightJourneys
 * - Matching journeys for each date range based on checkIn/checkOut dates
 * - Calculating flight prices using fare families (same as useCharterFlightPrice)
 * - Managing seatsLeft from journey data
 * - Bundle calculation is done in the parent hook
 *
 * @param params - Configuration parameters including dateRangesByMonth and charterBundleRoute
 * @returns Flight data by date
 */
export function useBundleFlightData(params: UseBundleFlightDataParams): BundleFlightData {
  const {
    rooms,
    dateRangesByMonth,
    fareClass,
    charterBundleRoute,
    disabled = false,
    seatEligiblePassengers,
  } = params

  const baseFareClass = toBaseFareClass(fareClass)

  // Get all charter flight journeys (same as useCharterFlightJourneysForHotelDates)
  const flightResults = useAllCharterFlightJourneys({
    charterBundleRoute,
    fareClass: baseFareClass,
    occupancies: rooms,
    disabled: disabled || !charterBundleRoute,
  })

  const journeysError = flightResults?.error
  const searchResultsTimestamp = flightResults?.timestamp

  // Extract all date ranges for fare families queries
  const allDateRanges = useMemo(() => {
    return Object.values(dateRangesByMonth).flat()
  }, [dateRangesByMonth])

  // Map flight search results - match journeys for each date range
  const flightDataByDate = useMemo(() => {
    const dataMap: Record<string, FlightDataByDate> = {}

    if (!flightResults?.fares) {
      // If no flight data yet, mark all as fetching
      allDateRanges.forEach(({ checkIn }) => {
        dataMap[checkIn] = {
          fetching: true,
        }
      })
      return dataMap
    }

    const departingFares = flightResults.fares[0] ?? []
    const returningFares = flightResults.fares[1] ?? []

    allDateRanges.forEach(({ checkIn, checkOut }) => {
      // Match departing journey by checkIn date (arrival date)
      const departingJourney = checkIn ? departingFares.find(journey => {
        const arrivalDate = journey.flightGroup.flights.at(-1)?.arrivalDate
        return arrivalDate && moment(arrivalDate).format('YYYY-MM-DD') === checkIn
      }) : undefined

      // Match returning journey by checkOut date (departure date)
      const returningJourney = checkOut ? returningFares.find(journey => {
        const departingDate = journey.flightGroup.flights.at(0)?.departingDate
        return departingDate && moment(departingDate).format('YYYY-MM-DD') === checkOut
      }) : undefined

      if (departingJourney && returningJourney) {
        // Calculate seatsLeft from both journeys
        const departingSeatsLeft = getSeatsLeftFromJourney(departingJourney)
        const returningSeatsLeft = getSeatsLeftFromJourney(returningJourney)
        const seatsLeft = departingSeatsLeft !== undefined && returningSeatsLeft !== undefined
          ? Math.min(departingSeatsLeft, returningSeatsLeft)
          : departingSeatsLeft ?? returningSeatsLeft
        const departingPromotionSeatsLeft = getPromotionSeatsLeftFromJourney(departingJourney)
        const returningPromotionSeatsLeft = getPromotionSeatsLeftFromJourney(returningJourney)
        const promotionSeatsLeft = Math.max(departingPromotionSeatsLeft ?? 0, returningPromotionSeatsLeft ?? 0)
        const departingFnfSeatsLeft = getFnfSeatsLeftFromJourney(departingJourney)
        const returningFnfSeatsLeft = getFnfSeatsLeftFromJourney(returningJourney)
        const fnfSeatsLeft = departingFnfSeatsLeft !== undefined && returningFnfSeatsLeft !== undefined
          ? Math.min(departingFnfSeatsLeft, returningFnfSeatsLeft)
          : departingFnfSeatsLeft ?? returningFnfSeatsLeft
        const isFnfPriceApplied = isRoundTripFnfPriceApplied(
          departingJourney,
          returningJourney,
          seatEligiblePassengers,
        )
        const isPromotionPriceApplied = !isFnfPriceApplied && !!(
          departingJourney.promotionPrice || returningJourney.promotionPrice
        )

        const departingFare = isFnfPriceApplied || !!departingJourney.promotionPrice?.adult.totalFare
          ? (getEffectiveFlightPrice(departingJourney, seatEligiblePassengers).all.totalFare ?? 0)
          : (getJourneyOriginalTotalFareForFareClass({
              journey: departingJourney,
              fareClass: baseFareClass,
            }) ?? 0)
        const returningFare = isFnfPriceApplied || returningJourney.promotionPrice?.adult.totalFare
          ? (getEffectiveFlightPrice(returningJourney, seatEligiblePassengers).all.totalFare ?? 0)
          : (getJourneyOriginalTotalFareForFareClass({
              journey: returningJourney,
              fareClass: baseFareClass,
            }) ?? 0)
        const originalDepartingFare = getJourneyOriginalTotalFareForFareClass({
          journey: departingJourney,
          fareClass: baseFareClass,
        }) ?? 0
        const originalReturningFare = getJourneyOriginalTotalFareForFareClass({
          journey: returningJourney,
          fareClass: baseFareClass,
        }) ?? 0
        const totalFare = departingFare + returningFare
        const originalTotalFare = originalDepartingFare + originalReturningFare

        dataMap[checkIn] = {
          fetching: false,
          totalPrice: totalFare,
          originalTotalPrice: originalTotalFare,
          journey: departingJourney,
          departingJourney,
          returningJourney,
          seatsLeft,
          promotionSeatsLeft,
          isPromotionPriceApplied,
          fnfSeatsLeft,
          isFnfPriceApplied,
        }
      } else {
        // No matching journeys found, check if search is complete
        const hasSearchCompleted = !!searchResultsTimestamp || !!journeysError
        dataMap[checkIn] = {
          fetching: !hasSearchCompleted,
        }
      }
    })

    return dataMap
  }, [allDateRanges, baseFareClass, flightResults, searchResultsTimestamp, journeysError, seatEligiblePassengers])

  return {
    flightDataByDate,
  }
}
